import React from 'react';

import { Alert, Dialog, DialogButtonType } from '@ast/magma';

import { OverlaySpinner } from '@app/common/components/overlayspinner';
import { useIsMobile } from '@app/common/utils/hooks/useIsMobile';

import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';

type PasskeyRevokeModalProps = {
  isLoadingRevokePasskey: boolean;
  passkeyRevokeError: string;
  setRevokeModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  onDeletePasskey: () => Promise<unknown>
};
export const PasskeyRevokeModal: React.FC<PasskeyRevokeModalProps> = ({
  isLoadingRevokePasskey,
  passkeyRevokeError,
  setRevokeModalOpen,
  onDeletePasskey,
}) => {
  const { t } = useUserSettingsTranslation();

  const isMobile = useIsMobile();

  return (
    <Dialog
      data-stable-name="DeletePasskeyDialog"
      predefinedWidth="md"
      align={isMobile ? 'bottom' : 'center'}
      title={t(
        'passkey-revoke.revoke-dialog.title|Delete passkey',
        'Delete passkey',
      )}
      showClose={{
        'aria-label': t(
          'passkey-revoke.revoke-dialog.close-button|Close delete passkey dialog',
          'Close delete passkey dialog',
        ),
      }}
      buttons={[{
        label: t(
          'passkey-revoke.revoke-dialog.cancel-button|Cancel',
          'Cancel',
        ),
        // eslint-disable-next-line i18next/no-literal-string
        'data-stable-name': 'ApproveCancelButton',
        type: DialogButtonType.Cancel,
      }, {
        label: t(
          'passkey-revoke.revoke-dialog.delete-button|Delete',
          'Delete',
        ),
        // eslint-disable-next-line i18next/no-literal-string
        'data-stable-name': 'ApproveDeleteButton',
        type: DialogButtonType.OK,
        onClick: async () => {
          await onDeletePasskey();
          return !passkeyRevokeError;
        },
      }]}
      defaultButton={DialogButtonType.OK}
      onClose={() => setRevokeModalOpen(false)}
    >
      <>
        {isLoadingRevokePasskey && (
          <OverlaySpinner
            visible
          />
        )}

        {passkeyRevokeError && (
        <Alert
            // eslint-disable-next-line i18next/no-literal-string
          level="error"
          data-stable-name="PasskeyRevokeError"
        >
          {passkeyRevokeError}
        </Alert>
        )}
        <p>
          {t(
            'passkey-revoke.revoke-dialog.text|Revoke dialog text',
            'By removing this passkey, you will no longer be able to use it to sign in to your account from any device where it is synced.',
          )}
        </p>
        <p>
          {t(
            'passkey-revoke.revoke-dialog.note|Revoke dialog note',
            'Note: You may continue to see the passkey as an option during sign-in until you also delete it from your browser, device, or password management software.',
          )}
        </p>
        <p>
          {t(
            'passkey-revoke.revoke-dialog.confirmation|Revoke dialog confirmation',
            'Are you sure you want to delete your passkey?',
          )}
        </p>
      </>
    </Dialog>

  );
};
